import { z } from 'zod';

import { IsoDate, Page, PageQuery, Platform, Uid } from '../primitives';
import { SessionDTO } from '../sessions';
import { TextFilter } from './common';
import { GrantDTO } from './grants';

/** A `devices` row (docs/02 §1). `id` is sha256(ANDROID_ID + salt) or a web UUID. */
export const DeviceDTO = z.object({
  id: z.string(),
  platform: Platform,
  model: z.string().nullable(),
  appVersion: z.string().nullable(),
  firstSeenAt: IsoDate,
  lastSeenAt: IsoDate,
  blocked: z.boolean(),
  notes: z.string().nullable(),
  sessionCount: z.number().int().nonnegative(),
  /** Pending, non-deleted grants targeting this device id (the "N pendientes" chip of CMS-11). */
  pendingGrants: z.number().int().nonnegative(),
});
export type DeviceDTO = z.infer<typeof DeviceDTO>;

/** `GET /cms/devices?q=` */
export const CmsDevicesQuery = PageQuery.extend({
  q: TextFilter.optional(),
  platform: Platform.optional(),
  blocked: z.enum(['yes', 'no']).optional(),
  /** Only devices seen in the last N days (CMS-11 "Última vez"). */
  seenDays: z.coerce.number().int().min(1).max(3650).optional(),
});
export type CmsDevicesQuery = z.infer<typeof CmsDevicesQuery>;
export type CmsDevicesQueryInput = z.input<typeof CmsDevicesQuery>;

/** The three counters at the top of CMS-11. */
export const DeviceCounts = z.object({
  total: z.number().int().nonnegative(),
  androidTv: z.number().int().nonnegative(),
  web: z.number().int().nonnegative(),
});
export type DeviceCounts = z.infer<typeof DeviceCounts>;

export const CmsDevicesResponse = Page(DeviceDTO).extend({ counts: DeviceCounts });
export type CmsDevicesResponse = z.infer<typeof CmsDevicesResponse>;

export const DeviceParams = z.object({ id: z.string().min(1).max(128) });
export type DeviceParams = z.infer<typeof DeviceParams>;

/** `PATCH /cms/devices/:id` (block, notes) */
export const CmsDevicePatchBody = z
  .object({
    blocked: z.boolean(),
    notes: z.string().max(2000).nullable(),
  })
  .partial()
  .refine((body) => Object.keys(body).length > 0, { message: 'At least one field is required' });
export type CmsDevicePatchBody = z.infer<typeof CmsDevicePatchBody>;

export const CmsDeviceResponse = z.object({ device: DeviceDTO });
export type CmsDeviceResponse = z.infer<typeof CmsDeviceResponse>;

/** Host of the most recent session on the device ("Último usuario" of CMS-11). */
export const CmsDeviceUserDTO = z.object({
  id: Uid,
  email: z.string().nullable(),
  displayName: z.string().nullable(),
});
export type CmsDeviceUserDTO = z.infer<typeof CmsDeviceUserDTO>;

/** `GET /cms/devices/:id` */
export const CmsDeviceDetailResponse = z.object({
  device: DeviceDTO,
  sessions: z.array(SessionDTO),
  pendingGrants: z.array(GrantDTO),
  lastUser: CmsDeviceUserDTO.nullable(),
});
export type CmsDeviceDetailResponse = z.infer<typeof CmsDeviceDetailResponse>;
